/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { api, mutatingPost, put, type ApiResult } from "./client";
import { PAGE_SIZE } from "./registry";
import { withSort, type SortChoice } from "../lib/sorting";

/** Mirror of nexus core.care.Coverage; only a care coordinator carries one. */
export type Coverage = "PRIMARY" | "SUPPORT";

export const COVERAGE_OPTIONS: { value: Coverage; label: string }[] = [
  { value: "SUPPORT", label: "Support" },
  { value: "PRIMARY", label: "Primary" },
];

export function coverageLabel(coverage: Coverage): string {
  return COVERAGE_OPTIONS.find((option) => option.value === coverage)?.label ?? coverage;
}

/**
 * Mirror of nexus core.care.CareTeamKind: the kind a care team holds an account as, from its
 * primary role, so a Therapist who is also a CareCoordinator is a THERAPIST.
 */
export type CareTeamKind = "THERAPIST" | "PARENT_COACH" | "CARE_COORDINATOR" | "BILLING_SPECIALIST";

/** Mirror of nexus AccountResponses.CareTeamAssignmentView: one record listing the member. */
export type CareTeamAssignment = {
  id: string;
  clientUserId: number;
  staffUserId: number;
  kind: CareTeamKind;
  primary: boolean;
  coverage: Coverage | null;
};

/**
 * Mirror of nexus AccountResponses.CareTeamMemberView. {@code familyCoverage} is a coordinator's
 * coverage of the whole family (nexus Coverage.ofFamily), null for any other member: a SUPPORT
 * coordinator answers the family's messages but is never its lead.
 */
export type CareTeamMember = {
  staffUserId: number;
  firstName: string | null;
  lastName: string | null;
  kind: CareTeamKind;
  familyCoverage: Coverage | null;
  onOwnRecord: boolean;
  viaParentIds: number[];
  assignments: CareTeamAssignment[];
};

/** Mirror of nexus AccountResponses.ChildCareTeamView: one child's derived team. */
export type ChildCareTeam = { childUserId: number; members: CareTeamMember[] };

/** Mirror of nexus CareTeamRoutes.FamilyCareTeamResponse. */
export type FamilyCareTeam = { familyId: number; children: ChildCareTeam[] };

/** Mirror of nexus CareTeamRoutes.CareTeamChangeResponse: the queued Healthie operations. */
export type CareTeamChange = { operationIds: string[]; note: string };

/** Mirror of nexus CareTeamService.StaffFamily. */
export type StaffFamily = { familyId: number; childUserIds: number[] };

/** Mirror of nexus CareTeamRoutes.StaffFamiliesResponse. */
export type StaffFamiliesPage = {
  items: StaffFamily[];
  hasMore: boolean;
  page: number;
  pageSize: number;
};

/** What covering every active family would change: exactly the families an assign would touch. */
export type CoveragePlan = {
  staffUserId: number;
  coverage: Coverage;
  familyCount: number;
  childCount: number;
  families: StaffFamily[];
};

export type CoverageRunStatus = "PENDING" | "OK" | "FAILED";

/** A queued coverage run's progress, read from its internal event. */
export type CoverageRun = {
  eventId: string;
  staffUserId: number;
  coverage: Coverage;
  status: CoverageRunStatus;
  total: number;
  processed: number;
  /** Families whose Healthie changes went to the outbox, not changes Healthie has applied. */
  queued: number;
  alreadyCovered: number;
  refused: { familyId: number; reason: string }[];
  lastError: string | null;
  emittedAt: string;
  processedAt: string | null;
};

export function familyCareTeam(familyId: number): Promise<ApiResult<FamilyCareTeam>> {
  return api<FamilyCareTeam>(`/v1/accounts/families/${familyId}/care-team`);
}

/**
 * Adds the member to every active child's own record; a coverage only for a coordinator. A retry
 * of the same add passes the same idempotency key.
 */
export function addToCareTeam(
  familyId: number,
  staffUserId: number,
  coverage: Coverage | null,
  idempotencyKey: string,
): Promise<ApiResult<CareTeamChange>> {
  return api<CareTeamChange>(
    `/v1/accounts/families/${familyId}/care-team`,
    mutatingPost(coverage === null ? { staffUserId } : { staffUserId, coverage }, idempotencyKey),
  );
}

/** Removes the member from every record in the family that lists them, parents' included. */
export function removeFromCareTeam(
  familyId: number,
  staffUserId: number,
): Promise<ApiResult<CareTeamChange>> {
  return api<CareTeamChange>(`/v1/accounts/families/${familyId}/care-team/${staffUserId}`, {
    method: "DELETE",
  });
}

export function setCoverage(
  assignmentId: string,
  coverage: Coverage,
): Promise<ApiResult<CareTeamAssignment>> {
  return api<CareTeamAssignment>(`/v1/care-team/${assignmentId}/coverage`, put({ coverage }));
}

export function staffFamilies(
  staffId: string,
  page: number,
  sort: SortChoice,
): Promise<ApiResult<StaffFamiliesPage>> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
  withSort(params, sort);
  return api<StaffFamiliesPage>(`/v1/accounts/${staffId}/care-team?${params.toString()}`);
}

export function planCoverage(
  staffId: string,
  coverage: Coverage,
): Promise<ApiResult<CoveragePlan>> {
  return api<CoveragePlan>(
    `/v1/accounts/${staffId}/coverage`,
    mutatingPost({ coverage, dryRun: true }),
  );
}

/**
 * Queues one paced run over the reviewed families; progress is read with {@link coverageRun}. One
 * idempotency key per reviewed plan. Refused (409) while the member's previous run is pending.
 */
export function applyCoverage(
  staffId: string,
  coverage: Coverage,
  familyIds: number[],
  idempotencyKey: string,
): Promise<ApiResult<{ eventId: string; status: "QUEUED" }>> {
  return api<{ eventId: string; status: "QUEUED" }>(
    `/v1/accounts/${staffId}/coverage`,
    mutatingPost({ coverage, dryRun: false, familyIds }, idempotencyKey),
  );
}

export function coverageRun(staffId: string, eventId: string): Promise<ApiResult<CoverageRun>> {
  return api<CoverageRun>(`/v1/accounts/${staffId}/coverage/${eventId}`);
}
